// Shared recursive logical/asset tree (Org → Site → Building → Floor → Zone →
// Device, also Groups). One source of truth so /sites, /cameras and the
// contract coverage picker render the SAME tree — icons, expand/collapse,
// health colors. Optional checkbox mode (for coverage selection) + an optional
// per-node trailing slot. No drag-drop here (that stays page-specific).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Boxes, MapPin, Building2, Layers, Square, Tag, Cctv, HardDrive, FolderTree, ChevronDown,
} from 'lucide-react';
import type { TreeNode, TreeNodeType } from '@/lib/inventory-api';
import { StatusPill } from '@/components/ui/primitives';

export const NODE_ICON: Record<TreeNodeType, typeof Building2> = {
  organization: Boxes, site: MapPin, building: Building2, floor: Layers,
  zone: Square, group: Tag, device: Cctv, driver: HardDrive, unassigned: FolderTree,
};
export const scoreColor = (s: number) => (s >= 90 ? 'text-ok' : s >= 70 ? 'text-warning' : 'text-critical');

export function defaultExpandedIds(roots: TreeNode[], depth = 2): Set<string> {
  const ids = new Set<string>();
  const walk = (nodes: TreeNode[], d: number) => {
    for (const n of nodes) {
      if (d < depth && n.children.length) { ids.add(n.id); walk(n.children, d + 1); }
    }
  };
  walk(roots, 0);
  return ids;
}

export interface LogicalTreeProps {
  roots: TreeNode[];
  /** Selection (browse mode). */
  selectedId?: string | null;
  onSelect?: (node: TreeNode) => void;
  /** Health score + status pill on the right (browse mode). Default true. */
  showHealth?: boolean;
  /** Checkbox mode: render a checkbox per node. */
  checkable?: boolean;
  isChecked?: (node: TreeNode) => boolean;
  onCheck?: (node: TreeNode, checked: boolean) => void;
  /** Only these node types may be checked (others render disabled / no box). */
  checkableTypes?: TreeNodeType[];
  /** Optional trailing controls for a (checked) node — e.g. system-type select. */
  renderNodeExtra?: (node: TreeNode) => React.ReactNode;
}

function Row({
  node, depth, expanded, toggle, props,
}: {
  node: TreeNode; depth: number;
  expanded: Set<string>; toggle: (id: string) => void;
  props: LogicalTreeProps;
}) {
  const { t } = useTranslation('components');
  const Icon = NODE_ICON[node.type] ?? FolderTree;
  const hasChildren = node.children.length > 0;
  const open = expanded.has(node.id);
  const active = node.id === props.selectedId;
  const isDevice = node.type === 'device';
  const showHealth = props.showHealth !== false;
  const canCheck = props.checkable && (!props.checkableTypes || props.checkableTypes.includes(node.type));
  const checked = canCheck && props.isChecked?.(node);

  return (
    <li>
      <div
        className={`group flex items-center gap-2 rounded-md py-1.5 pr-2 transition-colors ${active ? 'bg-white/10' : 'hover:bg-white/5'}`}
        style={{ paddingLeft: depth * 18 + 4 }}
      >
        {hasChildren ? (
          <button onClick={() => toggle(node.id)} className="grid h-5 w-5 place-items-center rounded text-muted hover:text-fg" aria-label={open ? t('logicalTree.collapse') : t('logicalTree.expand')}>
            <ChevronDown size={14} className={`transition-transform ${open ? '' : '-rotate-90'}`} />
          </button>
        ) : <span className="w-5" />}

        {props.checkable && (
          canCheck ? (
            <input
              type="checkbox" checked={!!checked}
              onChange={(e) => props.onCheck?.(node, e.target.checked)}
              className="h-4 w-4 shrink-0 accent-[var(--tk-primary,theme(colors.blue.500))]"
            />
          ) : <span className="w-4 shrink-0" />
        )}

        <button
          onClick={() => props.onSelect?.(node)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          disabled={!props.onSelect}
        >
          <Icon size={15} className={isDevice ? scoreColor(node.score) : 'text-muted'} />
          <span className="truncate text-fg">{node.name}</span>
          {!isDevice && <span className="text-[11px] text-muted">({node.deviceCount})</span>}
        </button>

        {checked && props.renderNodeExtra?.(node)}

        {showHealth && (
          <>
            <span className={`tk-num text-xs font-medium ${scoreColor(node.score)}`}>{node.score}%</span>
            <StatusPill status={node.status} />
          </>
        )}
      </div>
      {hasChildren && open && (
        <ul>
          {node.children.map((c) => (
            <Row key={c.id} node={c} depth={depth + 1} expanded={expanded} toggle={toggle} props={props} />
          ))}
        </ul>
      )}
    </li>
  );
}

export function LogicalTree(props: LogicalTreeProps) {
  const { t } = useTranslation('components');
  const [expanded, setExpanded] = useState<Set<string>>(() => defaultExpandedIds(props.roots));
  const toggle = (id: string) =>
    setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  if (!props.roots.length) {
    return <p className="py-8 text-center text-sm text-muted">{t('logicalTree.empty')}</p>;
  }
  return (
    <ul className="text-sm">
      {props.roots.map((n) => (
        <Row key={n.id} node={n} depth={0} expanded={expanded} toggle={toggle} props={props} />
      ))}
    </ul>
  );
}
